// ---------------------------------------------------------------------------
// Investment property projection engine (Task 1077).
//
// Pure, dependency-free math behind the multi-page "Investment Property
// Report" PDF export. Takes the buyer's WYSIWYG Year-1 operating figures (the
// exact numbers the underwriting panel displays) plus financing + long-range
// growth assumptions, and produces a year-by-year buy & hold projection:
// income/expenses/cash flow, loan amortization, depreciation & interest tax
// deductions, appreciation-driven equity, and a full if-sold analysis (sale
// proceeds, cumulative profit, IRR, ROI, equity multiple) for every year of
// the horizon.
//
// Conventions:
// - All *Pct inputs are whole percents (6.5 = 6.5%), never fractions.
// - Year 1 income/expense figures are used VERBATIM (WYSIWYG contract with the
//   on-screen panel); later years scale them by the growth assumptions.
// - Ratios that would divide by zero come back null (rendered as "—").
// - All-cash purchases pass `financing: null`: debt rows are all zero and
//   debt-derived ratios (DSCR, debt yield, LTV) are null.
// ---------------------------------------------------------------------------

/**
 * Loan shape for the purchase debt (Task 1084):
 * - "conventional": fully-amortizing for `amortYears` (the default).
 * - "interest_only_balloon": hard money / bridge — interest-only payments on a
 *   flat balance through the IO period, balloon at `balloonYear`.
 * - "seller_financing": amortizes on the `amortYears` schedule with the
 *   remaining balance due at `balloonYear`.
 * For both balloon shapes, years past the balloon assume the remaining balance
 * refinances into a conventional amortizing loan at `refiRatePct` over
 * `refiAmortYears` (defaults: same rate / 30 years). A sale at or before the
 * balloon simply pays the balance out of proceeds, exactly as today.
 */
export type ProjectionLoanType =
  | "conventional"
  | "interest_only_balloon"
  | "seller_financing";

export interface ProjectionFinancing {
  /** Down payment as a percent of purchase price (25 = 25%). */
  downPct: number;
  /** Annual interest rate percent (6.5 = 6.5%). */
  ratePct: number;
  /** Amortization schedule in years (unused during an IO period). */
  amortYears: number;
  /** Loan shape; missing/undefined = "conventional" (legacy payloads). */
  loanType?: ProjectionLoanType;
  /** Balloon year (balance due at END of this year). Non-conventional only. */
  balloonYear?: number | null;
  /**
   * Interest-only period in years (interest_only_balloon only). null =
   * interest-only all the way to the balloon. When shorter than the balloon
   * year, the loan amortizes on `amortYears` between IO end and the balloon.
   */
  ioYears?: number | null;
  /** Post-balloon refinance rate percent. null = same as `ratePct`. */
  refiRatePct?: number | null;
  /** Post-balloon refinance amortization in years. null = 30. */
  refiAmortYears?: number | null;
}

export interface ProjectionInput {
  /** Purchase price (the buyer's basis for the deal). */
  price: number;
  /** Capital needs / rehab funded in cash at close. ARV = price + capital. */
  capitalTotal: number;
  /** One-time purchase (closing) costs as a percent of price (3 = 3%). */
  purchaseCostsPct: number;
  /** Loan terms, or null for an all-cash purchase. */
  financing: ProjectionFinancing | null;
  /** Year-1 operating figures, exactly as shown in the underwriting panel. */
  year1: {
    /** Annual gross rent (grown base gross income). */
    grossRent: number;
    /** Annual other income (value-add streams + expense recoveries). */
    otherIncome: number;
    /** Annual vacancy & credit loss in dollars (not a percent). */
    vacancyLoss: number;
    /** Annual operating expenses (gross, never reduced by recoveries). */
    opexTotal: number;
  };
  growth: {
    /** Annual income growth percent applied from year 2 on. */
    incomePctYr: number;
    /** Annual expense growth percent applied from year 2 on. */
    expensePctYr: number;
    /** Annual property appreciation percent (applied to ARV). */
    appreciationPctYr: number;
  };
  /** Selling costs at disposition as a percent of sale price (6 = 6%). */
  sellingCostsPct: number;
  depreciation: {
    /** Straight-line recovery period: 27.5 (residential) or 39 (commercial). */
    years: number;
    /** Land value excluded from the depreciable basis. */
    landValue: number;
  };
  /** Projection horizon in years (default 30). */
  horizonYears?: number;
  /** Optional exit assumptions; absent means the legacy appreciation model. */
  exit?: ProjectionExitSettings;
}

export type ProjectionValuationMethod = "exit_cap" | "appreciation";

export interface ProjectionExitSettings {
  valuationMethod?: ProjectionValuationMethod;
  /** Selected hold period, clamped to 1..30 (default 30). */
  holdYears?: number;
  /** Alias accepted for persisted clients that call the field a period. */
  holdPeriodYears?: number;
  /** Delta from the hold-period suggested exit cap, in basis points. */
  exitCapAdjustmentBps?: number;
  /** Percent-point form of the adjustment (1 = 100 bps), for compatibility. */
  exitCapAdjustmentPct?: number;
}

export interface ProjectionYear {
  year: number;
  // Operating
  grossRent: number;
  otherIncome: number;
  vacancyLoss: number;
  operatingIncome: number; // EGI = grossRent + otherIncome − vacancyLoss
  operatingExpenses: number;
  noi: number;
  /** Following year's NOI used for end-of-year exit-cap valuation. */
  forwardNoi: number;
  // Debt
  debtService: number;
  interestPaid: number;
  principalPaid: number;
  loanBalance: number; // end of year
  /**
   * True on the balloon year of an IO/seller-finance loan: the remaining
   * balance is due at the end of this year and (if held) refinances into the
   * conventional refi schedule from the following year on.
   */
  isBalloonYear: boolean;
  // Cash flow
  cashFlow: number; // NOI − debt service
  cumulativeCashFlow: number;
  cashOnCash: number | null; // cashFlow ÷ total cash invested
  // Tax deductions (informational; no tax-rate modeling)
  depreciation: number;
  totalDeductions: number; // interest + depreciation
  // Value & equity (end of year)
  propertyValue: number;
  equity: number; // value − balance
  ltv: number | null; // balance ÷ value
  // Ratios
  capRateOnPurchase: number | null; // NOI ÷ price, percent
  capRateOnValue: number | null; // NOI ÷ current value, percent
  dscr: number | null;
  debtYield: number | null; // NOI ÷ original loan amount, percent
  breakEvenRatio: number | null; // (opex + debt service) ÷ gross income, percent
  returnOnEquity: number | null; // cashFlow ÷ equity, percent
  // If sold at end of this year
  salePrice: number;
  /** Exit cap used for this sale year; null in appreciation mode. */
  exitCapRatePct: number | null;
  sellingCosts: number;
  saleProceeds: number; // salePrice − sellingCosts − loanBalance
  totalProfitIfSold: number; // proceeds + cumulative cash flow − cash invested
  roiIfSold: number | null; // totalProfit ÷ cash invested, percent
  equityMultiple: number | null; // (proceeds + cum cash flow) ÷ cash invested
  irr: number | null; // annualized IRR percent if sold end of this year
}

export interface ProjectionSummary {
  price: number;
  purchaseCosts: number;
  capitalTotal: number;
  arv: number; // price + capital
  allCash: boolean;
  loanAmount: number;
  downPayment: number;
  totalCashInvested: number; // down payment + purchase costs + capital
  /** Initial (Year-1) monthly payment — the IO payment for IO loans. */
  monthlyPayment: number;
  /** Initial (Year-1) annual debt service (12 × monthlyPayment). */
  annualDebtService: number;
  // Loan shape (resolved; "conventional" + nulls when all-cash/legacy)
  loanType: ProjectionLoanType;
  balloonYear: number | null;
  ioYears: number | null; // resolved IO period (IO loans only)
  refiRatePct: number | null; // resolved refi assumption (balloon loans only)
  refiAmortYears: number | null;
  /** Post-refi monthly payment; null until/unless the balloon falls inside the horizon. */
  refiMonthlyPayment: number | null;
  depreciableBasis: number;
  annualDepreciation: number;
  // Year-1 quick ratios
  grm: number | null; // price ÷ year-1 gross rent
  rentToValue: number | null; // monthly gross rent ÷ price, percent
  /** Resolved disposition assumptions and selected-hold sensitivity. */
  selectedHoldYears: number;
  goingInCapRatePct: number | null;
  suggestedExitCapPct: number | null;
  impliedExitCapPct: number | null;
  exitCapWarning: boolean;
  exitCapSensitivity: ProjectionExitSensitivity[];
  selectedHoldResult: ProjectionYear | null;
}

export interface ProjectionExitSensitivity {
  adjustmentBps: number;
  saleValue: number;
  salePrice: number;
  irr: number | null;
  equityMultiple: number | null;
}

export interface ProjectionResult {
  summary: ProjectionSummary;
  years: ProjectionYear[];
}

/** Product convention: add 10 bps (0.10 percentage points) per hold year. */
export function suggestedExitCapPct(
  goingInCapRatePct: number,
  holdYears: number,
): number {
  return goingInCapRatePct + 0.1 * Math.max(1, Math.min(30, Math.round(holdYears)));
}

const isFiniteNum = (v: number): boolean =>
  typeof v === "number" && Number.isFinite(v);
const nz = (v: number): number => (isFiniteNum(v) ? v : 0);

/** Standard fully-amortizing monthly payment (0 when unfinanced). */
export function amortizedMonthlyPayment(
  principal: number,
  annualRatePct: number,
  amortYears: number,
): number {
  const p = nz(principal);
  const r = nz(annualRatePct) / 100 / 12;
  const n = Math.round(nz(amortYears) * 12);
  if (p <= 0 || n <= 0) return 0;
  if (r === 0) return p / n; // 0% edge case (e.g. some seller carries)
  const f = Math.pow(1 + r, n);
  return (p * r * f) / (f - 1);
}

/**
 * Internal rate of return (annual, percent) for a cash-flow series where
 * `flows[0]` is the time-0 investment (negative) and `flows[t]` lands at the
 * end of year t. Solved by bisection on NPV; returns null when no sign change
 * exists in (−99%, 1000%) — e.g. every flow is negative.
 */
export function irrPct(flows: number[]): number | null {
  if (flows.length < 2) return null;
  const npv = (rate: number): number =>
    flows.reduce((sum, cf, t) => sum + cf / Math.pow(1 + rate, t), 0);
  let lo = -0.99;
  let hi = 10;
  let fLo = npv(lo);
  let fHi = npv(hi);
  if (!Number.isFinite(fLo) || !Number.isFinite(fHi)) return null;
  if (fLo * fHi > 0) return null; // no sign change → no bracketed root
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const fMid = npv(mid);
    if (!Number.isFinite(fMid)) return null;
    if (Math.abs(fMid) < 1e-7) return mid * 100;
    if (fLo * fMid <= 0) {
      hi = mid;
      fHi = fMid;
    } else {
      lo = mid;
      fLo = fMid;
    }
  }
  return ((lo + hi) / 2) * 100;
}

export function computeInvestmentProjection(
  input: ProjectionInput,
): ProjectionResult {
  const horizon = Math.max(1, Math.round(input.horizonYears ?? 30));
  const price = Math.max(0, nz(input.price));
  const capitalTotal = Math.max(0, nz(input.capitalTotal));
  const purchaseCosts = price * (Math.max(0, nz(input.purchaseCostsPct)) / 100);
  const arv = price + capitalTotal;

  const fin = input.financing;
  const allCash = fin == null;
  const downPct = fin ? Math.min(100, Math.max(0, nz(fin.downPct))) : 100;
  const loanAmount = allCash ? 0 : price * (1 - downPct / 100);
  const downPayment = price - loanAmount;
  const totalCashInvested = downPayment + purchaseCosts + capitalTotal;

  // --- Loan shape (Task 1084) ---------------------------------------------
  // Resolve the loan type + balloon/IO/refi assumptions. Legacy payloads carry
  // no loanType → conventional, which reproduces the original schedule bit-
  // for-bit (single amortizing phase, no balloon).
  const loanType: ProjectionLoanType = allCash
    ? "conventional"
    : (fin?.loanType ?? "conventional");
  const hasBalloon = !allCash && loanType !== "conventional";
  const balloonYear = hasBalloon
    ? Math.max(1, Math.round(nz(fin!.balloonYear ?? 5) || 5))
    : null;
  // IO period (IO loans only): null = interest-only all the way to the
  // balloon; an explicit shorter period amortizes between IO end and balloon.
  const ioYearsResolved =
    loanType === "interest_only_balloon"
      ? Math.min(
          balloonYear!,
          Math.max(1, Math.round(nz(fin!.ioYears ?? balloonYear!) || balloonYear!)),
        )
      : null;
  const ioMonths = ioYearsResolved != null ? ioYearsResolved * 12 : 0;
  const balloonMonth = balloonYear != null ? balloonYear * 12 : Infinity;
  const refiRatePct = hasBalloon
    ? Number.isFinite(fin!.refiRatePct ?? NaN)
      ? Math.max(0, fin!.refiRatePct as number)
      : nz(fin!.ratePct)
    : null;
  const refiAmortYears = hasBalloon
    ? Number.isFinite(fin!.refiAmortYears ?? NaN) &&
      (fin!.refiAmortYears as number) > 0
      ? Math.round(fin!.refiAmortYears as number)
      : 30
    : null;

  const monthlyRate = fin ? nz(fin.ratePct) / 100 / 12 : 0;
  // Amortizing payment on the original schedule. Also correct after an IO
  // period: the balance is still the full loan amount when amortization
  // starts, so the payment computed at origination amortizes it over
  // `amortYears` from that point.
  const basePayment = fin
    ? amortizedMonthlyPayment(loanAmount, fin.ratePct, fin.amortYears)
    : 0;
  // Year-1 actual payment: interest-only for IO loans, amortizing otherwise.
  // Every Year-1 ratio (cash-on-cash, DSCR, debt yield, break-even) flows from
  // the per-year debtService below, which uses the same schedule.
  const monthlyPayment =
    ioMonths > 0 ? loanAmount * monthlyRate : basePayment;
  const annualDebtService = monthlyPayment * 12;

  // Straight-line depreciation on (price + capital − land), never negative.
  const depYears = Math.max(0, nz(input.depreciation.years));
  const depreciableBasis = Math.max(
    0,
    price + capitalTotal - Math.max(0, nz(input.depreciation.landValue)),
  );
  const annualDepreciation = depYears > 0 ? depreciableBasis / depYears : 0;

  const y1 = input.year1;
  const growInc = 1 + nz(input.growth.incomePctYr) / 100;
  const growExp = 1 + nz(input.growth.expensePctYr) / 100;
  const growVal = 1 + nz(input.growth.appreciationPctYr) / 100;
  const sellingCostsPct = Math.max(0, nz(input.sellingCostsPct)) / 100;
  const valuationMethod: ProjectionValuationMethod =
    input.exit?.valuationMethod ?? "appreciation";
  const selectedHoldYears = Math.max(
    1,
    Math.min(
      30,
      Math.round(
        nz(input.exit?.holdYears ?? input.exit?.holdPeriodYears ?? 30) || 30,
      ),
    ),
  );

  const grm = nz(y1.grossRent) > 0 ? price / nz(y1.grossRent) : null;
  const rentToValue =
    price > 0 ? ((nz(y1.grossRent) / 12 / price) * 100) : null;
  const goingInCapRatePct = price > 0 ? (nz(y1.grossRent) + nz(y1.otherIncome) - nz(y1.vacancyLoss) - nz(y1.opexTotal)) / price * 100 : null;
  const suggestedCap =
    goingInCapRatePct == null
      ? null
      : suggestedExitCapPct(goingInCapRatePct, selectedHoldYears);
  const adjustmentBps = Number.isFinite(input.exit?.exitCapAdjustmentBps)
    ? (input.exit?.exitCapAdjustmentBps as number)
    : nz(input.exit?.exitCapAdjustmentPct ?? 0) * 100;
  const selectedExitCap =
    suggestedCap == null ? null : suggestedCap + adjustmentBps / 100;

  const years: ProjectionYear[] = [];
  let balance = loanAmount;
  let remainingBasis = depreciableBasis;
  let cumulativeCashFlow = 0;
  const cashFlows: number[] = [-totalCashInvested];
  // Global month counter driving the loan phases; refi payment is computed
  // lazily from the actual balance the month after the balloon.
  let monthIndex = 0;
  let refiMonthlyPayment: number | null = null;
  let refiMonthlyRate = 0;

  for (let year = 1; year <= horizon; year++) {
    const fi = Math.pow(growInc, year - 1);
    const fe = Math.pow(growExp, year - 1);
    const grossRent = nz(y1.grossRent) * fi;
    const otherIncome = nz(y1.otherIncome) * fi;
    const vacancyLoss = nz(y1.vacancyLoss) * fi;
    const operatingIncome = grossRent + otherIncome - vacancyLoss;
    const operatingExpenses = nz(y1.opexTotal) * fe;
    const noi = operatingIncome - operatingExpenses;

    // Step the loan schedule month by month so the final partial year (and
    // any mid-schedule phase change: IO end, balloon refi) settles exactly.
    // Phases, driven by the global month index:
    //   1..ioMonths            → interest-only, flat balance
    //   ioMonths+1..balloonMonth → amortizing on the original schedule
    //   balloonMonth+1..        → refinanced conventional loan (balance at the
    //                             balloon re-amortized at refi rate/term)
    let interestPaid = 0;
    let principalPaid = 0;
    let debtService = 0;
    if (!allCash && loanAmount > 0) {
      for (let m = 0; m < 12 && balance > 1e-9; m++) {
        monthIndex++;
        let rate = monthlyRate;
        let payment: number;
        if (monthIndex > balloonMonth) {
          if (refiMonthlyPayment == null) {
            // Balloon hit at the end of last month: the remaining balance
            // refinances into a conventional amortizing loan.
            refiMonthlyPayment = amortizedMonthlyPayment(
              balance,
              refiRatePct!,
              refiAmortYears!,
            );
            refiMonthlyRate = refiRatePct! / 100 / 12;
          }
          rate = refiMonthlyRate;
          payment = refiMonthlyPayment;
        } else if (monthIndex <= ioMonths) {
          // Interest-only: pay interest, balance stays flat.
          const interest = balance * rate;
          interestPaid += interest;
          debtService += interest;
          continue;
        } else {
          payment = basePayment;
        }
        const interest = balance * rate;
        const principal = Math.min(Math.max(0, payment - interest), balance);
        interestPaid += interest;
        principalPaid += principal;
        debtService += interest + principal;
        balance -= principal;
      }
      if (balance < 1e-6) balance = 0;
    }

    const cashFlow = noi - debtService;
    cumulativeCashFlow += cashFlow;
    cashFlows.push(cashFlow);

    const depreciation = Math.min(annualDepreciation, remainingBasis);
    remainingBasis -= depreciation;

    const appreciationValue = arv * Math.pow(growVal, year);

    const grossIncomeTotal = grossRent + otherIncome;
    // Exit-cap sales capitalize the following year's NOI. This deliberately
    // computes the extra horizon NOI without adding it to the displayed years.
    const nextFi = Math.pow(growInc, year);
    const nextFe = Math.pow(growExp, year);
    const nextNoi =
      (nz(y1.grossRent) + nz(y1.otherIncome) - nz(y1.vacancyLoss)) * nextFi -
      nz(y1.opexTotal) * nextFe;
    const annualExitCap =
      goingInCapRatePct == null
        ? null
        : suggestedExitCapPct(goingInCapRatePct, year) + adjustmentBps / 100;
    const salePrice =
      valuationMethod === "exit_cap" &&
      annualExitCap != null &&
      annualExitCap > 0
        ? nextNoi / (annualExitCap / 100)
        : appreciationValue;
    // The active valuation method is authoritative everywhere. In exit-cap
    // mode this keeps value, equity, charts, ratios, and sale proceeds aligned
    // to the same forward-NOI capitalization rather than mixing in appreciation.
    const propertyValue = salePrice;
    const equity = propertyValue - balance;
    const sellingCosts = salePrice * sellingCostsPct;
    const saleProceeds = salePrice - sellingCosts - balance;
    const totalProfitIfSold =
      saleProceeds + cumulativeCashFlow - totalCashInvested;

    const irr =
      totalCashInvested > 0
        ? irrPct([
            ...cashFlows.slice(0, year), // t0 investment + years 1..(n−1)
            cashFlows[year] + saleProceeds, // final year cash flow + sale
          ])
        : null;

    years.push({
      year,
      grossRent,
      otherIncome,
      vacancyLoss,
      operatingIncome,
      operatingExpenses,
      noi,
      forwardNoi: nextNoi,
      debtService,
      interestPaid,
      principalPaid,
      loanBalance: balance,
      isBalloonYear: balloonYear != null && year === balloonYear,
      cashFlow,
      cumulativeCashFlow,
      cashOnCash:
        totalCashInvested > 0 ? (cashFlow / totalCashInvested) * 100 : null,
      depreciation,
      totalDeductions: interestPaid + depreciation,
      propertyValue,
      equity,
      ltv: !allCash && propertyValue > 0 ? (balance / propertyValue) * 100 : null,
      capRateOnPurchase: price > 0 ? (noi / price) * 100 : null,
      capRateOnValue: propertyValue > 0 ? (noi / propertyValue) * 100 : null,
      dscr: debtService > 0 ? noi / debtService : null,
      debtYield: !allCash && loanAmount > 0 ? (noi / loanAmount) * 100 : null,
      breakEvenRatio:
        grossIncomeTotal > 0
          ? ((operatingExpenses + debtService) / grossIncomeTotal) * 100
          : null,
      returnOnEquity: equity > 0 ? (cashFlow / equity) * 100 : null,
      salePrice,
      exitCapRatePct:
        valuationMethod === "exit_cap" ? annualExitCap : null,
      sellingCosts,
      saleProceeds,
      totalProfitIfSold,
      roiIfSold:
        totalCashInvested > 0
          ? (totalProfitIfSold / totalCashInvested) * 100
          : null,
      equityMultiple:
        totalCashInvested > 0
          ? (saleProceeds + cumulativeCashFlow) / totalCashInvested
          : null,
      irr,
    });
  }

  const selectedYear = years[selectedHoldYears - 1] ?? years[years.length - 1];
  const selectedNoi = selectedYear
    ? (nz(y1.grossRent) + nz(y1.otherIncome) - nz(y1.vacancyLoss)) *
        Math.pow(growInc, selectedYear.year) -
      nz(y1.opexTotal) * Math.pow(growExp, selectedYear.year)
    : 0;
  const sensitivityAdjustments = [-50, 0, 50];
  const exitCapSensitivity: ProjectionExitSensitivity[] =
    valuationMethod === "exit_cap" && selectedExitCap != null && selectedYear
      ? sensitivityAdjustments.map((delta) => {
          const cap = selectedExitCap + delta / 100;
          const saleValue = cap > 0 ? selectedNoi / (cap / 100) : 0;
          const sellingCosts = saleValue * sellingCostsPct;
          const proceeds = saleValue - sellingCosts - selectedYear.loanBalance;
          const flows = [
            -totalCashInvested,
            ...cashFlows.slice(1, selectedYear.year),
            cashFlows[selectedYear.year] + proceeds,
          ];
          return {
            adjustmentBps: delta,
            saleValue,
            salePrice: saleValue,
            irr: totalCashInvested > 0 ? irrPct(flows) : null,
            equityMultiple:
              totalCashInvested > 0
                ? (proceeds + selectedYear.cumulativeCashFlow) /
                  totalCashInvested
                : null,
          };
        })
      : [];
  const impliedExitCapPct =
    selectedYear && selectedYear.salePrice > 0
      ? (selectedNoi / selectedYear.salePrice) * 100
      : null;

  return {
    summary: {
      price,
      purchaseCosts,
      capitalTotal,
      arv,
      allCash,
      loanAmount,
      downPayment,
      totalCashInvested,
      monthlyPayment,
      annualDebtService,
      loanType,
      balloonYear,
      ioYears: ioYearsResolved,
      refiRatePct,
      refiAmortYears,
      refiMonthlyPayment,
      depreciableBasis,
      annualDepreciation,
      grm,
      rentToValue,
       selectedHoldYears,
       goingInCapRatePct,
       suggestedExitCapPct: suggestedCap,
       impliedExitCapPct,
       exitCapWarning:
         valuationMethod === "appreciation" &&
         impliedExitCapPct != null &&
         goingInCapRatePct != null &&
         impliedExitCapPct < goingInCapRatePct - 0.25,
       exitCapSensitivity,
        selectedHoldResult: selectedYear ?? null,
    },
    years,
  };
}
